import { ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/16/solid';
import { createFileRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';
import { Card } from '../components/Card';
import { Button } from '../components/catalyst-ui-kit/button';
import { NavbarItem } from '../components/catalyst-ui-kit/navbar';
import { DepositModal } from '../components/DepositModal';
import {
  FundingHistoryIcon,
  OrdersIcon,
  OverviewIcon,
  PositionsIcon,
  TradeHistoryIcon,
  TradingSessionsIcon,
} from '../components/PortfolioNavIcons/PortfolioNavIcons';
import { SlidingTabs } from '../components/SlidingTabs';
import { Tooltip } from '../components/Tooltip/Tooltip';
import { WithdrawButton } from '../components/WithdrawButton/WithdrawButton';
import { WithdrawModal } from '../components/WithdrawModal/WithdrawModal';
import {
  PORTFOLIO_FUNDING_ROUTE_URL,
  PORTFOLIO_ORDERS_ROUTE_URL,
  PORTFOLIO_OVERVIEW_ROUTE_URL,
  PORTFOLIO_POSITIONS_ROUTE_URL,
  PORTFOLIO_SESSIONS_ROUTE_URL,
  PORTFOLIO_TRADES_ROUTE_URL,
} from '../constants/routes';
import { useModalState } from '../hooks/useModalState';
import { fundingHistoryComingSoonTooltip } from '../lib/tooltips';

export const Route = createFileRoute('/portfolio')({
  component: Index,
  beforeLoad: (ctx) => {
    if (ctx.location.pathname === '/portfolio/' || ctx.location.pathname === '/portfolio') {
      throw redirect({ to: PORTFOLIO_OVERVIEW_ROUTE_URL });
    }
  },
});

const PORTFOLIO_ROUTES = [
  { id: PORTFOLIO_OVERVIEW_ROUTE_URL, name: 'Overview', disabled: false, Icon: OverviewIcon },
  { id: PORTFOLIO_POSITIONS_ROUTE_URL, name: 'Positions', disabled: false, Icon: PositionsIcon },
  { id: PORTFOLIO_ORDERS_ROUTE_URL, name: 'Open Orders', disabled: false, Icon: OrdersIcon },
  {
    id: PORTFOLIO_TRADES_ROUTE_URL,
    name: 'Trade History',
    disabled: false,
    Icon: TradeHistoryIcon,
  },
  {
    id: PORTFOLIO_FUNDING_ROUTE_URL,
    name: 'Funding History',
    disabled: true,
    Icon: FundingHistoryIcon,
    tooltip: fundingHistoryComingSoonTooltip(),
  },
  {
    id: PORTFOLIO_SESSIONS_ROUTE_URL,
    name: 'Trading Sessions',
    disabled: false,
    Icon: TradingSessionsIcon,
  },
];

function Index() {
  // Determine the current active tab
  const location = useRouterState({ select: (s) => s.location });
  const activeTabId =
    PORTFOLIO_ROUTES.find((route) => route.id === location.pathname)?.id ||
    PORTFOLIO_OVERVIEW_ROUTE_URL;

  const setDepositModal = useModalState((state) => state.setDepositModal);

  return (
    <>
      <div className="flex grow gap-1">
        <Card className="hidden w-[230px] shrink-0 flex-col py-4 md:flex">
          <SlidingTabs
            tabs={PORTFOLIO_ROUTES}
            defaultActiveTab={activeTabId}
            containerClass="w-full"
            vertical={true}
            renderTab={({ tab, props, onClick }) => {
              const { Icon } = tab;
              return (
                <NavbarItem
                  {...props}
                  to={tab.id}
                  className={twMerge(
                    'px-2',
                    'border-l-2 border-transparent',
                    tab.disabled ? 'cursor-not-allowed opacity-50 hover:dark:bg-none' : ''
                  )}
                  linkClassName="p-2"
                  disabled={tab.disabled}
                  onClick={(e) => {
                    if (tab.disabled) {
                      e.preventDefault();
                    } else {
                      onClick();
                    }
                  }}
                >
                  <div className="flex size-5 items-center justify-center">
                    <Icon />
                  </div>
                  <Tooltip content={tab.tooltip}>{tab.name}</Tooltip>
                </NavbarItem>
              );
            }}
          />
          <div className="mt-auto space-y-2 px-4">
            <Button
              size="sm"
              color="blue"
              className="w-full"
              onClick={() => setDepositModal({ openOrClosed: 'open', context: 'Portfolio' })}
            >
              <ArrowDownTrayIcon /> Deposit
            </Button>
            <WithdrawButton size="sm" className="w-full" icon={<ArrowUpTrayIcon />} />
          </div>
        </Card>
        <div className="flex w-full min-w-0 grow flex-col">
          <Outlet />
        </div>
      </div>

      <DepositModal context="Portfolio" />
      <WithdrawModal />
    </>
  );
}
